import { type DeviceFragment, DeviceGeneralStatus, DeviceType } from '@cobliteam/api-lib/src/__generated__/graphql';

import { DeviceGeneralType, DeviceVehicleAssociationStatus, DisablerStatus, VehicleStatus } from '@cobliteam/common-types';
import type { History } from 'history';

const updateUrlSearchParam = (paramName: string, history: History, values?: string[] | string) => {
  const urlParams = new URLSearchParams(location.search);

  if (values && values.length > 0) {
    const paramValue = Array.isArray(values) ? values.join(',') : values;
    urlParams.set(paramName, paramValue);
  } else {
    urlParams.delete(paramName);
  }

  history.replace({ search: `?${urlParams.toString()}` });
};

const isAssociated = (device: DeviceFragment) => !!device.vehicle;

const getDeviceVehicleAssociationStatus = (device: DeviceFragment) => {
  const { vehicle, generalStatus } = device;
  if (vehicle?.id) {
    return DeviceVehicleAssociationStatus.ASSOCIATED;
  }

  if (generalStatus === DeviceGeneralStatus.LosingData) {
    return DeviceVehicleAssociationStatus.LOSING_DATA;
  }

  return DeviceVehicleAssociationStatus.NOT_ASSOCIATED;
};

const getDeviceVehicleStatus = (device: DeviceFragment, disablerStatus: DisablerStatus): VehicleStatus => {
  if (device.lastLocation && device.lastLocation.isPlugged === false) {
    return VehicleStatus.REMOVED;
  }
  if (device.type === DeviceType.Blocker && disablerStatus === DisablerStatus.Disabled) {
    return VehicleStatus.DISABLED;
  }
  if (device.type === DeviceType.Immobilizer) {
    switch (disablerStatus) {
      case DisablerStatus.Disabling:
      case DisablerStatus.Disabled:
        return VehicleStatus.DISABLED;
      case DisablerStatus.DisablePending:
        return VehicleStatus.DISABLE_PENDING;
      case DisablerStatus.EnablePending:
        return VehicleStatus.ENABLE_PENDING;
      default:
        break;
    }
  }
  if (device?.lastLocation?.ignitionOn) {
    return VehicleStatus.ON;
  }
  return VehicleStatus.OFF;
};

const getGeneralTypeEnum = (generalType: string | undefined) => DeviceGeneralType[generalType as keyof typeof DeviceGeneralType] || DeviceGeneralType.UNKNOWN;

const DEVICE_TYPE_TO_I18N_KEY: Record<DeviceType, string> = {
  [DeviceType.Blocker]: 'installed_with_blocker',
  [DeviceType.Buzzer]: 'installed',
  [DeviceType.Can]: 'can_network',
  [DeviceType.CanIbutton]: 'can_network_with_identifier',
  [DeviceType.CanIbuttonImmobillizer]: 'can_network_with_ignition_lock',
  [DeviceType.Dashcam]: 'camera',
  [DeviceType.IButton]: 'installed_with_identifier',
  [DeviceType.IButtonImmobilizer]: 'installed_with_ignition_lock_identifier',
  [DeviceType.Immobilizer]: 'installed_with_ignition_lock',
  [DeviceType.Multicam]: 'multicam',
  [DeviceType.Obd]: 'obd',
  [DeviceType.ObdRemovable]: 'removable',
  [DeviceType.PlugAndPlayBuzzer]: 'removable',
  [DeviceType.Solar]: 'others',
  [DeviceType.Unknown]: 'others',
};

const getDeviceTypeI18nKey = (type: DeviceType | undefined) => (type && DEVICE_TYPE_TO_I18N_KEY[type]) || 'others';

const getDeviceStatus = (device: DeviceFragment) => {
  const { generalStatus } = device;
  if (
    generalStatus === DeviceGeneralStatus.Damaged ||
    generalStatus === DeviceGeneralStatus.LosingData ||
    generalStatus === DeviceGeneralStatus.Unavailable ||
    generalStatus === DeviceGeneralStatus.NoSufficientData
  ) {
    return 'critical';
  }
  if (generalStatus === DeviceGeneralStatus.Disconnected || generalStatus === DeviceGeneralStatus.NoSignal || generalStatus === DeviceGeneralStatus.InDevolution) {
    return 'check';
  }
  return 'healthy';
};

export { updateUrlSearchParam, isAssociated, getDeviceVehicleAssociationStatus, getDeviceVehicleStatus, getGeneralTypeEnum, getDeviceTypeI18nKey, getDeviceStatus };
